/**
 * DripScribe Main JavaScript Entry Point
 *
 * This file imports and initializes all JavaScript modules.
 * It also exposes utilities to the global window object for use
 * in Django templates with inline scripts.
 */

// Import SCSS (Vite will handle this)
import '../scss/main.scss';

// Import utility modules
import { StatusPoller } from './utils/status-poller.js';
import { BulkSelector } from './utils/bulk-selector.js';
import { exportToCSV, downloadFile } from './utils/export.js';
import { PasswordValidator } from './utils/password-validator.js';
import templateData from './utils/template-data.js';

// Import component modules
import CampaignProgress from './components/campaign-progress.js';
import campaignDetail from './components/campaign-detail.js';
import campaignSample from './components/campaign-sample.js';
import DemoGenerator from './components/demo-generator.js';

// Import base template modules (SweetAlert config, mobile sidebar)
import swalConfig from './components/swal-config.js';
import mobileSidebar from './components/mobile-sidebar.js';

// Import credit warning system (shows toast at 20%/10% credits remaining)
import './components/credit-warnings.js';

// ============================================================================
// Global Namespace
// ============================================================================

/**
 * DripScribe global namespace
 * All utilities are exposed here for use in Django templates
 */
window.DripScribe = window.DripScribe || {};

// Expose utilities to global namespace
window.DripScribe.StatusPoller = StatusPoller;
window.DripScribe.BulkSelector = BulkSelector;
window.DripScribe.exportToCSV = exportToCSV;
window.DripScribe.downloadFile = downloadFile;
window.DripScribe.PasswordValidator = PasswordValidator;
window.DripScribe.CampaignProgress = CampaignProgress;
window.DripScribe.campaignDetail = campaignDetail;
window.DripScribe.campaignSample = campaignSample;
window.DripScribe.templateData = templateData;
window.DripScribe.swal = swalConfig;
window.DripScribe.DemoGenerator = DemoGenerator;
window.DripScribe.mobileSidebar = mobileSidebar;

// Also expose directly to window for backward compatibility
window.StatusPoller = StatusPoller;
window.BulkSelector = BulkSelector;
window.exportToCSV = exportToCSV;
window.downloadFile = downloadFile;
window.CampaignProgress = CampaignProgress;

// ============================================================================
// Global Initialization
// ============================================================================

document.addEventListener('DOMContentLoaded', () => {
  console.log('[DripScribe] Application initialized');

  // Initialize any global behaviors here
  initializeTooltips();
  initializeSidebar();
  initializeAlerts();
});

/**
 * Initialize Bootstrap tooltips
 */
function initializeTooltips() {
  const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
  if (tooltipTriggerList.length > 0 && typeof bootstrap !== 'undefined') {
    tooltipTriggerList.forEach(el => new bootstrap.Tooltip(el));
  }
}

/**
 * Initialize sidebar toggle functionality
 */
function initializeSidebar() {
  const toggle = document.querySelector('[data-sidebar-toggle]');
  const sidebar = document.querySelector('.sidebar');
  const overlay = document.querySelector('.sidebar-overlay');

  if (toggle && sidebar) {
    toggle.addEventListener('click', () => {
      sidebar.classList.toggle('is-open');
      overlay?.classList.toggle('is-visible');
      document.body.classList.toggle('sidebar-open');
    });

    overlay?.addEventListener('click', () => {
      sidebar.classList.remove('is-open');
      overlay.classList.remove('is-visible');
      document.body.classList.remove('sidebar-open');
    });
  }
}

/**
 * Initialize auto-dismiss alerts
 */
function initializeAlerts() {
  const alerts = document.querySelectorAll('.alert[data-auto-dismiss]');
  alerts.forEach(alert => {
    const delay = parseInt(alert.dataset.autoDismiss) || 5000;
    setTimeout(() => {
      alert.classList.add('fade');
      setTimeout(() => alert.remove(), 150);
    }, delay);
  });
}

// ============================================================================
// Utility Functions (available globally)
// ============================================================================

/**
 * Get CSRF token from cookie (for AJAX requests)
 * @returns {string|null}
 */
window.getCSRFToken = function() {
  const name = 'csrftoken';
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
};

/**
 * Make a fetch request with CSRF token
 * @param {string} url
 * @param {object} options
 * @returns {Promise<Response>}
 */
window.fetchWithCSRF = async function(url, options = {}) {
  const csrfToken = window.getCSRFToken();

  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken,
    },
    credentials: 'same-origin',
  };

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  return fetch(url, mergedOptions);
};

/**
 * Format a number with commas
 * @param {number} num
 * @returns {string}
 */
window.formatNumber = function(num) {
  return new Intl.NumberFormat().format(num);
};

/**
 * Format a date string
 * @param {string} dateString
 * @param {object} options
 * @returns {string}
 */
window.formatDate = function(dateString, options = {}) {
  const date = new Date(dateString);
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(date);
};

/**
 * Debounce function
 * @param {Function} func
 * @param {number} wait
 * @returns {Function}
 */
window.debounce = function(func, wait = 300) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Show a toast notification
 * @param {string} message
 * @param {string} type - 'success' | 'error' | 'warning' | 'info'
 */
window.showToast = function(message, type = 'info') {
  // Create toast element
  const toast = document.createElement('div');
  toast.className = `toast toast--${type} animate-slide-down`;
  toast.innerHTML = `
    <div class="toast__content">
      <span class="toast__message">${message}</span>
      <button class="toast__close" onclick="this.parentElement.parentElement.remove()">×</button>
    </div>
  `;

  // Add to container (create if doesn't exist)
  let container = document.querySelector('.toast-container');
  if (!container) {
    container = document.createElement('div');
    container.className = 'toast-container';
    document.body.appendChild(container);
  }

  container.appendChild(toast);

  // Auto remove after 5 seconds
  setTimeout(() => {
    toast.classList.add('animate-fade-out');
    setTimeout(() => toast.remove(), 300);
  }, 5000);
};
